import { useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation } from '@tanstack/react-query'
import { useApiClient } from '../useApiClient'

/**
 * Mounts once at the post-AuthGuard layer (App.tsx) and, on the first render
 * where isAuthenticated becomes true, POSTs /rpc/sync_current_user so the
 * server can upsert the current user's row in api.auth0_user_mapping.
 *
 * Failure is logged at console.warn — non-blocking. The picker remains
 * functional via the manual sync script (infra/auth0-sync/sync-users.mjs).
 */
export function useSyncCurrentUser() {
  const { isAuthenticated } = useAuth0()
  const client = useApiClient()
  const fired = useRef(false)
  const mutation = useMutation({
    mutationFn: () => client.rpc<unknown>('sync_current_user', {}),
    onError: (err) => {
      console.warn('[useSyncCurrentUser] sync failed; current-user row may be stale:', err)
    },
  })

  useEffect(() => {
    if (!isAuthenticated || fired.current || mutation.isPending) return
    fired.current = true
    mutation.mutate()
  }, [isAuthenticated, mutation])

  return mutation
}
